import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type HomeCard, type HomeCardLine } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtCurrency, fmtNumber, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, RiskBandPill, TrustPill } from "@/components/dash/ui";

const SEV_DOT: Record<string, string> = {
  critical: "bg-rose-400", warning: "bg-amber-400", info: "bg-sky-400", ok: "bg-emerald-400", none: "bg-slate-600",
};

function fmtVal(line: HomeCardLine): string {
  const v = line.value;
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (line.unit === "EUR") return fmtCurrency(v);
  if (line.unit === "pct") return `${v > 0 ? "+" : ""}${v}%`;
  return fmtNumber(v);
}

function Badge({ kind, label }: { kind: string; label: string }) {
  if (kind === "risk") return <RiskBandPill band={label} />;
  if (kind === "trust") return <TrustPill label={label} />;
  return <span className="rounded-full bg-ink-700 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">{label}</span>;
}

function Card({ c }: { c: HomeCard }) {
  return (
    <div className="panel panel-pad flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${SEV_DOT[c.severity] ?? "bg-slate-600"}`} aria-hidden />
          <h3 className="text-sm font-semibold text-slate-100">{c.title}</h3>
          {c.severity !== "none" && c.severity !== "ok" && (
            <span className="text-[10px] uppercase tracking-wide text-slate-500">{c.severity}</span>
          )}
        </div>
        {c.count !== null && <span className="rounded-full bg-ink-700 px-2 py-0.5 text-[11px] text-slate-400">{c.count}</span>}
      </div>

      {c.lines.length > 0 && (
        <div className="space-y-0.5">
          {c.lines.map((l, i) => (
            <div key={i} className="flex items-baseline justify-between text-sm">
              <span className="text-slate-400">{l.label}</span>
              <span className="tabular-nums text-slate-100">{fmtVal(l)}</span>
            </div>
          ))}
        </div>
      )}

      {c.badges.length > 0 && <div className="flex flex-wrap gap-1.5">{c.badges.map((b, i) => <Badge key={i} kind={b.kind} label={b.label} />)}</div>}

      {c.notes.length > 0 && (
        <ul className="space-y-0.5 text-xs text-slate-500">
          {c.notes.map((n, i) =>
            typeof n === "string"
              ? (n ? <li key={i}>{n}</li> : null)
              : <li key={i}><Link to={n.link} className="text-brand-300 hover:text-brand-200">{n.text}</Link></li>,
          )}
        </ul>
      )}

      {c.empty && !c.lines.length && <p className="text-xs text-slate-600">No data available yet.</p>}

      <div className="mt-auto pt-1">
        <Link to={c.link} className="text-xs font-medium text-brand-300 hover:text-brand-200">View →</Link>
      </div>
    </div>
  );
}

export default function ExecutiveHomePage() {
  const qc = useQueryClient();
  const auth = useAuth();
  const [view, setView] = useState<string | undefined>(undefined);
  const q = useQuery({ queryKey: ["home", view], queryFn: () => dashApi.home(view) });
  const setDefault = useMutation({
    mutationFn: (v: string) => dashApi.setHomePreferences({ preferred_view: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["home"] }),
  });
  const data = q.data;
  const activeView = view ?? data?.view ?? "executive";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Executive Home"
        description="Your daily command centre — what's happening today, what changed, what needs action, and whether the numbers can be trusted. Each card links to the page that explains it."
        right={
          <div className="flex items-center gap-2">
            <Link to="/briefing" className="rounded-lg border border-ink-500/60 bg-ink-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-ink-700">Daily Briefing →</Link>
            {data && (
              <select value={activeView} onChange={(e) => setView(e.target.value)}
                className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
                {data.available_views.map((v) => <option key={v} value={v}>{v} view</option>)}
              </select>
            )}
            {data && activeView && (
              <button type="button" onClick={() => setDefault.mutate(activeView)} disabled={setDefault.isPending}
                className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-400 hover:bg-ink-700 disabled:opacity-60">
                {setDefault.isSuccess ? "Saved ✓" : "Make default"}
              </button>
            )}
          </div>
        }
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !data?.cards.length ? (
        <Panel title="Home"><EmptyState>No dashboard data available yet.</EmptyState></Panel>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.cards.map((c) => <Card key={c.key} c={c} />)}
          </div>
          <p className="text-[11px] text-slate-600">
            {activeView} view · generated {fmtRelative(data.generated_at)} · signed in as {auth.user?.email ?? "—"}. Read-only; figures are clearly labelled and repair value is kept separate from business sales.
          </p>
        </>
      )}
    </div>
  );
}
